import { useCallback, useEffect, useRef, useState } from 'react'
import { useSSE, type FrappeEvent } from '@/contexts/SSEContext'
import { useProfile } from '@/api/profile'
import { signalerFrappe } from '@/api/inbox'

/** Un signal toutes les 3 s au plus pendant la frappe. */
const INTERVALLE_ENVOI = 3000
/** Sans nouveau signal pendant 5 s, « X écrit… » disparaît. */
const DUREE_AFFICHAGE = 5000

/**
 * « Noah écrit… » : les collègues qui écrivent dans ce fil (texto ou note).
 * Rien n'est stocké : on garde en mémoire le dernier signal de chacun.
 * Sans ligne (fil de toutes les lignes), toute frappe pour cette personne compte.
 */
export function useColleguesQuiEcrivent(phone: string, line: string | null): string[] {
  const { onFrappe } = useSSE()
  const { data: profile } = useProfile()
  const moiId = profile?.id ?? null
  const [ecrivent, setEcrivent] = useState<Record<number, { name: string; jusqua: number }>>({})

  // Un autre fil : on repart de zéro.
  useEffect(() => setEcrivent({}), [phone, line])

  useEffect(() => {
    return onFrappe((f: FrappeEvent) => {
      if (f.user_id === moiId || f.phone !== phone) return
      if (line !== null && f.line !== line) return
      setEcrivent((prev) => ({ ...prev, [f.user_id]: { name: f.name, jusqua: Date.now() + DUREE_AFFICHAGE } }))
    })
  }, [onFrappe, phone, line, moiId])

  // Retire ceux qui se sont tus.
  useEffect(() => {
    const actifs = Object.values(ecrivent)
    if (!actifs.length) return
    const prochain = Math.min(...actifs.map((e) => e.jusqua)) - Date.now()
    const id = setTimeout(() => {
      const maintenant = Date.now()
      setEcrivent((prev) =>
        Object.fromEntries(Object.entries(prev).filter(([, e]) => e.jusqua > maintenant)),
      )
    }, Math.max(prochain, 50))
    return () => clearTimeout(id)
  }, [ecrivent])

  return Object.values(ecrivent).map((e) => e.name)
}

/** Fonction à appeler à chaque frappe : envoie au plus un signal toutes les 3 s. */
export function useSignalerFrappe(phone: string, line: string | null, actif: boolean) {
  const dernier = useRef(0)
  return useCallback(
    (mode: 'sms' | 'note') => {
      if (!actif) return
      const maintenant = Date.now()
      if (maintenant - dernier.current < INTERVALLE_ENVOI) return
      dernier.current = maintenant
      signalerFrappe(phone, line, mode)
    },
    [phone, line, actif],
  )
}
